import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

export interface TabDef { id: string; label: string; }

/**
 * <cadence-tabs .tabs=${[{id,label}]} active="id"> emits `tab-change` with the tab id.
 *
 * The bar is a single non-wrapping row that scrolls horizontally when the tabs
 * don't fit (e.g. Settings' dozen sections, or any page on a narrow viewport).
 * A soft edge fade signals there's more to scroll to; the active tab is kept in
 * view, and Arrow/Home/End keys move between tabs so off-screen ones stay reachable.
 */
@customElement('cadence-tabs')
export class CadenceTabs extends LitElement {
  @property({ type: Array }) tabs: TabDef[] = [];
  @property() active = '';
  @state() private fadeLeft = false;
  @state() private fadeRight = false;
  @query('.bar') private bar?: HTMLElement;
  private ro?: ResizeObserver;

  static styles = css`
    :host { display: block; }
    .bar {
      display: flex; gap: 4px;
      /* Bottom rule via inset shadow (not border) so it never adds to the scroll
         box — lets us pin overflow-y: hidden without clipping the active underline. */
      box-shadow: inset 0 -1px 0 var(--color-border);
      flex-wrap: nowrap; overflow-x: auto; overflow-y: hidden; scroll-behavior: smooth;
      scrollbar-width: none; -ms-overflow-style: none;
      /* The fade widths are driven by --fl/--fr (0 when that edge isn't clipped). */
      -webkit-mask-image: linear-gradient(to right, transparent 0, #000 var(--fl, 0px), #000 calc(100% - var(--fr, 0px)), transparent 100%);
      mask-image: linear-gradient(to right, transparent 0, #000 var(--fl, 0px), #000 calc(100% - var(--fr, 0px)), transparent 100%);
    }
    .bar::-webkit-scrollbar { display: none; }
    button {
      font: inherit; font-weight: 600; cursor: pointer; background: none; border: none;
      padding: 10px 16px; color: var(--color-text-secondary); white-space: nowrap; flex: 0 0 auto;
      border-bottom: 2px solid transparent;
    }
    button:hover { color: var(--color-text-primary); }
    button[aria-selected='true'] { color: var(--color-primary); border-bottom-color: var(--color-primary); }
    button:focus-visible { outline: 2px solid var(--color-primary); outline-offset: -2px; border-radius: var(--radius-sm); }
    @media (prefers-reduced-motion: reduce) { .bar { scroll-behavior: auto; } }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.ro = new ResizeObserver(() => this.measure());
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.ro?.disconnect();
  }

  firstUpdated(): void {
    if (this.bar) this.ro?.observe(this.bar);
    this.measure();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('active')) this.scrollActiveIntoView();
    if (changed.has('tabs')) this.measure();
  }

  /** Flag whichever edges are clipped so the fade only shows when it means "more". */
  private measure = () => {
    const el = this.bar;
    if (!el) return;
    this.fadeLeft = el.scrollLeft > 1;
    this.fadeRight = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
  };

  private scrollActiveIntoView(): void {
    const el = this.bar?.querySelector<HTMLElement>(`button[data-tab="${CSS.escape(this.active)}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  private select(id: string): void {
    if (id === this.active) return;
    this.active = id;
    this.dispatchEvent(new CustomEvent('tab-change', { detail: id, bubbles: true, composed: true }));
  }

  /** Roving keyboard navigation: Arrow keys step through tabs, Home/End jump to ends. */
  private onKeydown(e: KeyboardEvent): void {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = this.tabs.findIndex((t) => t.id === this.active);
    if (i < 0) return;
    const last = this.tabs.length - 1;
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? last
      : e.key === 'ArrowRight' ? Math.min(last, i + 1) : Math.max(0, i - 1);
    this.select(this.tabs[next].id);
    this.updateComplete.then(() => this.bar?.querySelector<HTMLElement>(`button[data-tab="${CSS.escape(this.tabs[next].id)}"]`)?.focus());
  }

  render() {
    return html`
      <div
        class="bar"
        role="tablist"
        style=${styleMap({ '--fl': this.fadeLeft ? '28px' : '0px', '--fr': this.fadeRight ? '28px' : '0px' })}
        @scroll=${this.measure}
        @keydown=${this.onKeydown}
      >
        ${this.tabs.map(
          (tab) => html`
            <button
              role="tab"
              data-tab=${tab.id}
              tabindex=${this.active === tab.id ? '0' : '-1'}
              aria-selected=${this.active === tab.id ? 'true' : 'false'}
              @click=${() => this.select(tab.id)}
            >${tab.label}</button>
          `,
        )}
      </div>
    `;
  }
}
